import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Provider, Region, Environment, OSType, Disk, DatabaseType,
  DatabaseEdition, Addon, AddonOption, PowerType, PowerAddon,
} from '../../types';
import { VMRow, DatabaseRow, OtherLineItem, PowerVMRow, PowerAddonLineItem, Totals } from './types';
import { formatCurrency, formatDiskSize } from '../../utils/formatCurrency';

/* ---------------- SVG-to-PNG helper for PDF logo ---------------- */
export const svgToPngDataUrl = (svgUrl: string): Promise<{ dataUrl: string; aspect: number } | null> =>
  new Promise(async (resolve) => {
    try {
      const resp = await fetch(svgUrl);
      const svgText = await resp.text();
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
      const svgEl = svgDoc.querySelector('svg');

      let aspect = 1;
      const vb = svgEl?.getAttribute('viewBox');
      if (vb) {
        const parts = vb.split(/[\s,]+/).map(Number);
        if (parts.length === 4 && parts[3]! > 0) aspect = parts[2]! / parts[3]!;
      } else {
        const w = parseFloat(svgEl?.getAttribute('width') ?? '0');
        const h = parseFloat(svgEl?.getAttribute('height') ?? '0');
        if (w > 0 && h > 0) aspect = w / h;
      }

      const canvasH = 400;
      const canvasW = Math.round(canvasH * aspect);
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, canvasW, canvasH);
        resolve({ dataUrl: canvas.toDataURL('image/png'), aspect });
      };
      img.onerror = () => resolve(null);
      img.src = svgUrl;
    } catch {
      resolve(null);
    }
  });

export interface GeneratePDFParams {
  logo: string;
  quoteName: string;
  providers: Provider[];
  regions: Region[];
  environments: Environment[];
  osTypes: OSType[];
  disks: Disk[];
  databaseTypes: DatabaseType[];
  databaseEditions: DatabaseEdition[];
  addons: Addon[];
  addonOptions: Record<number, AddonOption[]>;
  selectedProvider: number | string;
  selectedRegion: number | string;
  loadedQuoteId: number | null;
  username: string;
  vms: VMRow[];
  databases: DatabaseRow[];
  otherLineItems: OtherLineItem[];
  totals: Totals;
  // Price helpers
  calculateVMCost: (vm: VMRow) => number;
  getVMTypeId: (cpu: number, ram: number) => number | undefined;
  getDatabaseVMPrice: (vmTypeId: number | undefined, editionId: number | string, regionId: number | string, dbDisks: (number | null)[], environmentId: number) => number;
  getDatabasePrice: (configId: number | string, regionId: number | string) => number;
  getDatabaseTotalPrice: (configId: number | string, regionId: number | string, dailyBackups: number, extraStorageGB: number) => number;
  getDiskSizeGB: (diskId: number | null) => number;
  getBackupCostPerGB: (regionId: number | string) => number;
  getAddonCostByIds: (addonId: number, optionId: number | null) => number;
  // Availability checkers
  isVMRowAvailable: (vm: VMRow) => boolean;
  isPaasDbRowAvailable: (db: DatabaseRow) => boolean;
  isVmDbRowAvailable: (db: DatabaseRow) => boolean;
  isAddonRowAvailable: (item: OtherLineItem) => boolean;
  // Power VM
  powerVMs: PowerVMRow[];
  powerTypes: PowerType[];
  powerAddons: PowerAddon[];
  powerAddonItems: PowerAddonLineItem[];
  calculatePowerVMCost: (row: PowerVMRow) => number;
  getPowerAddonCost: (powerAddonId: number) => number;
  isPowerVMRowAvailable: (row: PowerVMRow) => boolean;
  isPowerAddonRowAvailable: (item: PowerAddonLineItem) => boolean;
}

export const generatePDF = async (params: GeneratePDFParams) => {
  const {
    logo, quoteName, providers, regions, environments, osTypes, disks,
    databaseTypes, databaseEditions, addons, addonOptions,
    selectedProvider, selectedRegion, loadedQuoteId, username,
    vms, databases, otherLineItems, totals,
    calculateVMCost, getVMTypeId, getDatabaseVMPrice, getDatabasePrice, getDatabaseTotalPrice,
    getDiskSizeGB, getBackupCostPerGB, getAddonCostByIds,
    isVMRowAvailable, isPaasDbRowAvailable, isVmDbRowAvailable, isAddonRowAvailable,
    powerVMs, powerTypes, powerAddons, powerAddonItems,
    calculatePowerVMCost, getPowerAddonCost,
    isPowerVMRowAvailable, isPowerAddonRowAvailable,
  } = params;

  const PRIMARY: [number, number, number] = [74, 74, 74];
  const ACCENT: [number, number, number] = [248, 237, 68];
  const DARK: [number, number, number] = [51, 51, 51];
  const GREY: [number, number, number] = [102, 102, 102];
  const LIGHT_BG: [number, number, number] = [245, 245, 245];
  const RED_TEXT: [number, number, number] = [220, 53, 69];
  const RED_BG: [number, number, number] = [255, 235, 238];

  const providerName = providers.find((p) => p.ProviderId === parseInt(String(selectedProvider), 10))?.ProviderName ?? '\u2014';
  const regionName = regions.find((r) => r.RegionId === parseInt(String(selectedRegion), 10))?.RegionName ?? '\u2014';
  const quoteIdDisplay = loadedQuoteId ?? '\u2014';
  const generatedUTC = new Date().toISOString().replace('T', ' ').replace(/\.\d+Z$/, ' UTC');

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;

  let logoResult: { dataUrl: string; aspect: number } | null = null;
  try { logoResult = await svgToPngDataUrl(logo); } catch { /* proceed without logo */ }

  const headerH = 28;
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageW, headerH, 'F');

  doc.setFillColor(...ACCENT);
  doc.rect(0, headerH, pageW, 1.5, 'F');

  const logoPad = 3;
  let textStartX = margin;
  if (logoResult) {
    const logoH = headerH - (logoPad * 2);
    const logoW = logoH * logoResult.aspect;
    doc.addImage(logoResult.dataUrl, 'PNG', margin, logoPad, logoW, logoH);
    textStartX = margin + logoW + 6;
  }

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(quoteName || 'Infrastructure Quote', textStartX, 12);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Provider: ${providerName}    Region: ${regionName}    Quote ID: ${quoteIdDisplay}`, textStartX, 19);
  doc.text(`Generated: ${generatedUTC}    User: ${username ?? '\u2014'}`, textStartX, 25);

  doc.setFillColor(237, 247, 237);
  doc.rect(0, 29.5, pageW, 12, 'F');

  doc.setTextColor(...DARK);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(`Monthly Total: ${formatCurrency(totals.monthly)}`, margin, 37.5);
  doc.text(`Annual Total: ${formatCurrency(totals.annual)}`, margin + 80, 37.5);

  let yPos = 48;
  let hasUnavailableItems = false;

  // Fixed column grid (mm) — all tables share this so Qty & Cost/mo always align
  // Total = 269mm (A4 landscape 297 - 14*2 margins)
  const CW = { name: 38, env: 22, cpu: 22, ram: 22, os: 45, storage: 55, qty: 25, cost: 40 };
  const colWidths8 = {
    0: { cellWidth: CW.name }, 1: { cellWidth: CW.env }, 2: { cellWidth: CW.cpu },
    3: { cellWidth: CW.ram }, 4: { cellWidth: CW.os }, 5: { cellWidth: CW.storage },
    6: { cellWidth: CW.qty, halign: 'right' as const }, 7: { cellWidth: CW.cost, halign: 'right' as const, fontStyle: 'bold' as const },
  };

  const autoTableDefaults = {
    startY: yPos,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8, cellPadding: 2, textColor: DARK, lineColor: [220, 220, 220] as [number, number, number], lineWidth: 0.2 },
    headStyles: { fillColor: PRIMARY, textColor: [255, 255, 255] as [number, number, number], fontStyle: 'bold' as const, fontSize: 8 },
    alternateRowStyles: { fillColor: LIGHT_BG },
    theme: 'grid' as const,
  };

  const unavailableCellHook = (indices: Set<number>) => (data: unknown) => {
    const d = data as { section: string; row: { index: number }; cell: { styles: Record<string, unknown> } };
    if (d.section === 'body' && indices.has(d.row.index)) { d.cell.styles.textColor = RED_TEXT; d.cell.styles.fillColor = RED_BG; }
  };

  const getLastY = () => ((doc as unknown as Record<string, Record<string, number>>).lastAutoTable?.finalY ?? yPos) + 8;

  const sectionTitle = (title: string) => {
    doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
    doc.text(title, margin, yPos); yPos += 2;
  };

  // Track unavailable row indices per table
  const unavailableVmIndices = new Set<number>();
  const unavailableDbIndices = new Set<number>();
  const unavailableOtherIndices = new Set<number>();

  // VMs table
  if (vms.length > 0) {
    const vmRows = vms.map((vm, idx) => {
      const envName = environments.find((e) => e.EnvironmentId === (vm.environmentId ?? 1))?.EnvironmentCode ?? '\u2014';
      const osName = osTypes.find((o) => o.OSTypeId === parseInt(String(vm.osTypeId), 10))?.OSName ?? '\u2014';
      const diskNames = (vm.disks ?? []).map((d) => { const dk = disks.find((x) => x.DiskId === d); return dk ? formatDiskSize(dk.DiskSize) : '\u2014'; }).join(', ') || '\u2014';
      if (!isVMRowAvailable(vm)) { unavailableVmIndices.add(idx); hasUnavailableItems = true; return [vm.name || '(unnamed)', envName, vm.cpu, `${vm.ram} GB`, osName, diskNames, vm.quantity, 'UNAVAILABLE']; }
      const cost = vm.savedLineCost != null ? vm.savedLineCost : calculateVMCost(vm);
      return [vm.name || '(unnamed)', envName, vm.cpu, `${vm.ram} GB`, osName, diskNames, vm.quantity, formatCurrency(cost)];
    });

    sectionTitle('Virtual Machines');
    autoTable(doc, { ...autoTableDefaults, startY: yPos, head: [['Name', 'Env', 'CPU', 'RAM', 'OS', 'Extra Storage', 'Qty', 'Cost/mo']], body: vmRows, columnStyles: colWidths8, didParseCell: unavailableCellHook(unavailableVmIndices) });
    yPos = getLastY();
  }

  // Databases table
  if (databases.length > 0) {
    const dbRows = databases.map((db, idx) => {
      const typeName = databaseTypes.find((t) => t.DatabaseTypeId === parseInt(String(db.databaseTypeId), 10))?.DatabaseName ?? '\u2014';
      if (db.deploymentType === 'vm_based') {
        const envName = environments.find((e) => e.EnvironmentId === (db.environmentId ?? 1))?.EnvironmentCode ?? '\u2014';
        const editionName = databaseEditions.find((e) => e.DatabaseEditionId === parseInt(String(db.editionId), 10))?.EditionName ?? '\u2014';
        const diskNames = (db.disks ?? []).map((d) => { const dk = disks.find((x) => x.DiskId === d); return dk ? formatDiskSize(dk.DiskSize) : '\u2014'; }).join(', ') || '\u2014';
        if (!isVmDbRowAvailable(db)) { unavailableDbIndices.add(idx); hasUnavailableItems = true; return [typeName, envName, db.cpu, `${db.ram} GB`, editionName, diskNames, db.quantity, 'UNAVAILABLE']; }
        let cost = db.savedLineCost != null ? db.savedLineCost : 0;
        if (db.savedLineCost == null) {
          const vmTypeId = getVMTypeId(db.cpu, db.ram);
          if (vmTypeId) {
            const perUnit = getDatabaseVMPrice(vmTypeId, db.editionId, selectedRegion, db.disks ?? [], db.environmentId ?? 1);
            const totalDiskGB = (db.disks ?? []).reduce((s: number, d) => s + getDiskSizeGB(d), 0);
            const backupCost = totalDiskGB * getBackupCostPerGB(selectedRegion) * Math.max(0, (db.dailyBackups ?? 0) - 1);
            cost = (perUnit + backupCost) * db.quantity;
          } else { cost = 0; }
        }
        return [typeName, envName, db.cpu, `${db.ram} GB`, editionName, diskNames, db.quantity, formatCurrency(cost)];
      }
      if (!isPaasDbRowAvailable(db)) { unavailableDbIndices.add(idx); hasUnavailableItems = true; return [typeName, '\u2014', db.cores, '\u2014', db.tier || '\u2014', '\u2014', db.quantity, 'UNAVAILABLE']; }
      const cost = db.savedLineCost != null ? db.savedLineCost : getDatabaseTotalPrice(db.databaseConfigId, selectedRegion, db.dailyBackups ?? 0, db.extraStorageGB ?? 0) * db.quantity;
      return [typeName, '\u2014', db.cores, '\u2014', db.tier || '\u2014', '\u2014', db.quantity, formatCurrency(cost)];
    });

    if (yPos > pageH - 40) { doc.addPage(); yPos = 14; }
    sectionTitle('Databases');
    autoTable(doc, { ...autoTableDefaults, startY: yPos, head: [['Component', 'Env', 'CPU/Cores', 'RAM', 'Edition/Tier', 'Extra Storage', 'Qty', 'Cost/mo']], body: dbRows, columnStyles: colWidths8, didParseCell: unavailableCellHook(unavailableDbIndices) });
    yPos = getLastY();
  }

  // Other Items table — uses same 8-col grid; Component & Option span multiple cols via empty spacers
  if (otherLineItems.length > 0) {
    const addonRows = otherLineItems.map((item, idx) => {
      const addonId = parseInt(String(item.itemId), 10);
      const addonName = addons.find((a) => a.AddonId === addonId)?.AddonName ?? '\u2014';
      const allOpts = addonId ? (addonOptions[addonId] ?? []) : [];
      const optName = item.optionId ? (allOpts.find((o) => o.AddonOptionId === parseInt(String(item.optionId), 10))?.OptionName ?? '\u2014') : '\u2014';
      if (!isAddonRowAvailable(item)) { unavailableOtherIndices.add(idx); hasUnavailableItems = true; return [addonName, optName, '', '', '', '', item.quantity, 'UNAVAILABLE']; }
      const cost = item.savedLineCost != null ? item.savedLineCost : (getAddonCostByIds(addonId, item.optionId ? parseInt(String(item.optionId), 10) : null) * item.quantity);
      return [addonName, optName, '', '', '', '', item.quantity, formatCurrency(cost)];
    });

    if (yPos > pageH - 40) { doc.addPage(); yPos = 14; }
    sectionTitle('Other Items');
    autoTable(doc, { ...autoTableDefaults, startY: yPos, head: [['Component', 'Option', '', '', '', '', 'Qty', 'Cost/mo']], body: addonRows, columnStyles: colWidths8, didParseCell: unavailableCellHook(unavailableOtherIndices) });
    yPos = getLastY();
  }

  // Power VMs table
  const unavailablePowerVmIndices = new Set<number>();
  if (powerVMs.length > 0) {
    const pvmRows = powerVMs.map((pvm, idx) => {
      const envName = environments.find((e) => e.EnvironmentId === (pvm.environmentId ?? 1))?.EnvironmentCode ?? '\u2014';
      const typeName = powerTypes.find((pt) => pt.PowerTypeId === parseInt(String(pvm.powerTypeId), 10))?.PowerTypeName ?? '\u2014';
      const diskNames = (pvm.disks ?? []).map((d) => { const dk = disks.find((x) => x.DiskId === d); return dk ? formatDiskSize(dk.DiskSize) : '\u2014'; }).join(', ') || '\u2014';
      if (!isPowerVMRowAvailable(pvm)) { unavailablePowerVmIndices.add(idx); hasUnavailableItems = true; return [pvm.name || '(unnamed)', envName, pvm.cpu.toFixed(2), `${pvm.ram} GB`, typeName, diskNames, pvm.quantity, 'UNAVAILABLE']; }
      const cost = pvm.savedLineCost != null ? pvm.savedLineCost : calculatePowerVMCost(pvm);
      return [pvm.name || '(unnamed)', envName, pvm.cpu.toFixed(2), `${pvm.ram} GB`, typeName, diskNames, pvm.quantity, formatCurrency(cost)];
    });

    if (yPos > pageH - 40) { doc.addPage(); yPos = 14; }
    sectionTitle('IBM Power VMs');
    autoTable(doc, { ...autoTableDefaults, startY: yPos, head: [['Name', 'Env', 'CPU', 'RAM', 'Power Type', 'Extra Storage', 'Qty', 'Cost/mo']], body: pvmRows, columnStyles: colWidths8, didParseCell: unavailableCellHook(unavailablePowerVmIndices) });
    yPos = getLastY();
  }

  // Power Addons table — same 8-col grid with spacers
  const unavailablePowerAddonIndices = new Set<number>();
  if (powerAddonItems.length > 0) {
    const paRows = powerAddonItems.map((item, idx) => {
      const addonId = parseInt(String(item.powerAddonId), 10);
      const addonName = powerAddons.find((a) => a.PowerAddonId === addonId)?.PowerAddonName ?? '\u2014';
      if (!isPowerAddonRowAvailable(item)) { unavailablePowerAddonIndices.add(idx); hasUnavailableItems = true; return [addonName, '', '', '', '', '', item.quantity, 'UNAVAILABLE']; }
      const cost = item.savedLineCost != null ? item.savedLineCost : getPowerAddonCost(addonId) * item.quantity;
      const isOneTime = !!powerAddons.find((a) => a.PowerAddonId === addonId)?.IsOneTime;
      return [addonName, '', '', '', '', '', item.quantity, isOneTime ? `${formatCurrency(cost)} (one-time)` : formatCurrency(cost)];
    });

    if (yPos > pageH - 40) { doc.addPage(); yPos = 14; }
    sectionTitle('IBM Power Addons');
    autoTable(doc, { ...autoTableDefaults, startY: yPos, head: [['Component', '', '', '', '', '', 'Qty', 'Cost']], body: paRows, columnStyles: colWidths8, didParseCell: unavailableCellHook(unavailablePowerAddonIndices) });
    yPos = getLastY();
  }

  // Summary box
  if (yPos > pageH - 30) { doc.addPage(); yPos = 14; }

  const summaryHeight = totals.oneTime > 0 ? 28 : 20;
  doc.setFillColor(237, 247, 237);
  doc.roundedRect(pageW - margin - 90, yPos, 90, summaryHeight, 2, 2, 'F');

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.roundedRect(pageW - margin - 90, yPos, 90, summaryHeight, 2, 2, 'S');

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text('Monthly Total:', pageW - margin - 86, yPos + 8);
  doc.text(formatCurrency(totals.monthly), pageW - margin - 4, yPos + 8, { align: 'right' });
  doc.text('Annual Total:', pageW - margin - 86, yPos + 16);
  doc.text(formatCurrency(totals.annual), pageW - margin - 4, yPos + 16, { align: 'right' });
  if (totals.oneTime > 0) {
    doc.text('One-time Build:', pageW - margin - 86, yPos + 24);
    doc.text(formatCurrency(totals.oneTime), pageW - margin - 4, yPos + 24, { align: 'right' });
  }

  // Unavailable footnote
  if (hasUnavailableItems) {
    yPos += 26;
    if (yPos > pageH - 20) { doc.addPage(); yPos = 14; }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...RED_TEXT);
    doc.text('* Items marked UNAVAILABLE are not available for the selected provider/region and are excluded from totals.', margin, yPos);
  }

  // Footer
  const pageCount = (doc.internal as unknown as { getNumberOfPages: () => number }).getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(...GREY);
    doc.text('All prices shown are estimates and are subject to change. Final costs may vary based on actual usage, configuration changes, and provider pricing updates.', margin, pageH - 6);
    doc.text(`Page ${i} of ${pageCount}`, pageW - margin, pageH - 6, { align: 'right' });
  }

  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
  const filename = `CloudCalculator_${timestamp}.pdf`;
  doc.save(filename);
};
